import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { compact, range } from 'lodash-es';
import { isApproved, isSample, isTerminated, personOfOrdinal } from '~/models/relationship/selectors';
export function richTextContent(legalTerms) {
    if (isPDF(legalTerms)) {
        return [];
    }
    return typeof legalTerms.richTextContent[0] === 'string' ? legalTerms.richTextContent.map((v)=>JSON.parse(v)) : legalTerms.richTextContent;
}
export function isPDF(legalTerms) {
    return legalTerms.contentType === 'pdf';
}
export function isRichText(legalTerms) {
    return legalTerms.contentType === 'richText';
}
export function isQuill(legalTerms) {
    return legalTerms.contentType === 'quill';
}
export function isAnchorDefault(legalTerms) {
    return legalTerms.defaultVersion != null;
}
export function isLegacyAnchorDefault(legalTerms) {
    return legalTerms.defaultVersion === 'v1' || legalTerms.defaultVersion === 'v2';
}
export function isCurrentAnchorDefault(legalTerms) {
    return legalTerms.defaultVersion === 'v3';
}
export function wasMigrated(legalTerms) {
    return legalTerms.previousDefaultVersion != null;
}
export function isEditable(legalTerms) {
    return isQuill(legalTerms) && !isAnchorDefault(legalTerms);
}
export function isDuplicatable(legalTerms) {
    return isQuill(legalTerms) && !isAnchorDefault(legalTerms);
}
export function hasSignature(legalTerms) {
    return isPDF(legalTerms) && legalTerms.signature != null;
}
export function isSignatureSetupComplete(legalTerms) {
    return hasSignature(legalTerms) && legalTerms.signature.setupComplete;
}
export function hasSignatureField(signature) {
    var _signature_setupIssues;
    return !((_signature_setupIssues = signature.setupIssues) !== null && _signature_setupIssues !== void 0 ? _signature_setupIssues : []).includes('noSignatureField');
}
export function signatureSetupIssuesToFix(legalTerms) {
    var _legalTerms_signature_setupIssues;
    const setupIssues = hasSignature(legalTerms) ? (_legalTerms_signature_setupIssues = legalTerms.signature.setupIssues) !== null && _legalTerms_signature_setupIssues !== void 0 ? _legalTerms_signature_setupIssues : [] : [];
    return setupIssues.includes('noSignatureField') ? setupIssues.filter((issue)=>issue !== 'roleWithoutFields') : setupIssues;
}
export function withoutSignature(legalTerms) {
    if (!hasSignature(legalTerms)) return legalTerms;
    const { signature: _signature } = legalTerms, rest = _object_without_properties(legalTerms, [
        "signature"
    ]);
    return rest;
}
export function signatureDocsAsDisabledItems(legalTermsRows, tooltip) {
    return legalTermsRows.filter(hasSignature).map((row)=>({
            id: row.id,
            tooltip
        }));
}
export function signatureRoles(legalTerms) {
    return hasSignature(legalTerms) ? legalTerms.signature.roles : [];
}
export function documentName(document) {
    return document.displayName.replace(/\.pdf$/i, '');
}
export function requiresSignature(document) {
    return document.termsOfService.type === 'signature';
}
export function isMandatorySignature(document) {
    return document.termsOfService.type === 'signature' && document.termsOfService.signature.mandatory;
}
export function isSignatureCompletedBy(document, ordinal) {
    var _document_submission_completions, _document_submission;
    return document.type === 'pdf' && !!((_document_submission = document.submission) === null || _document_submission === void 0 ? void 0 : (_document_submission_completions = _document_submission.completions) === null || _document_submission_completions === void 0 ? void 0 : _document_submission_completions.some((c)=>c.ordinal === ordinal));
}
export function signatureCompletedOn(document, ordinal) {
    var _ref;
    var _document_submission_completions_find, _document_submission_completions, _document_submission;
    return document.type === 'pdf' ? (_ref = (_document_submission = document.submission) === null || _document_submission === void 0 ? void 0 : (_document_submission_completions = _document_submission.completions) === null || _document_submission_completions === void 0 ? void 0 : (_document_submission_completions_find = _document_submission_completions.find((completion)=>completion.ordinal === ordinal)) === null || _document_submission_completions_find === void 0 ? void 0 : _document_submission_completions_find.completedOn) !== null && _ref !== void 0 ? _ref : null : null;
}
export function hasCompletedSignatureDocument(document) {
    var _document_submission;
    return document.type === 'pdf' && ((_document_submission = document.submission) === null || _document_submission === void 0 ? void 0 : _document_submission.completedDocumentKey) != null;
}
export function hasSigningRole(document, ordinal) {
    var _ref;
    var _document_signature;
    return requiresSignature(document) && document.type === 'pdf' && document.submission != null && ordinal <= ((_ref = (_document_signature = document.signature) === null || _document_signature === void 0 ? void 0 : _document_signature.roles.length) !== null && _ref !== void 0 ? _ref : 0);
}
function signingRoleCount(document) {
    var _ref;
    var _document_signature;
    return document.type === 'pdf' ? (_ref = (_document_signature = document.signature) === null || _document_signature === void 0 ? void 0 : _document_signature.roles.length) !== null && _ref !== void 0 ? _ref : 0 : 0;
}
function submissionOf(document) {
    return document.type === 'pdf' ? document.submission : undefined;
}
function signingActionsOf(document) {
    var _ref, _ref1;
    const submission = submissionOf(document);
    return [
        ...(_ref = submission === null || submission === void 0 ? void 0 : submission.completions) !== null && _ref !== void 0 ? _ref : [],
        ...(_ref1 = submission === null || submission === void 0 ? void 0 : submission.skips) !== null && _ref1 !== void 0 ? _ref1 : []
    ];
}
function actedOrdinals(document) {
    return signingActionsOf(document).map((action)=>action.ordinal);
}
function actorOf(document, ordinal) {
    var _signingActionsOf_find;
    return (_signingActionsOf_find = signingActionsOf(document).find((action)=>action.ordinal === ordinal)) === null || _signingActionsOf_find === void 0 ? void 0 : _signingActionsOf_find.userId;
}
function isDefinedRole(document, ordinal) {
    return ordinal >= 1 && ordinal <= signingRoleCount(document);
}
function isFinalized(relationship) {
    return isApproved(relationship) || isTerminated(relationship);
}
export function signingDocumentsOf(relationship) {
    return isSample(relationship) ? [] : relationship.legalTermsDocuments.filter(requiresSignature);
}
export function boundSigningOrdinals(document, relationship) {
    const roleOrdinals = range(1, signingRoleCount(document) + 1);
    const acted = actedOrdinals(document);
    return roleOrdinals.filter((ordinal)=>acted.includes(ordinal) || !isFinalized(relationship) && 'name' in personOfOrdinal(relationship, ordinal));
}
function isFullySigned(document, relationship) {
    const bound = boundSigningOrdinals(document, relationship);
    return bound.length > 0 && bound.every((ordinal)=>isSignatureCompletedBy(document, ordinal));
}
export function awaitsSignedCopy(document, relationship) {
    return isFullySigned(document, relationship) && !hasCompletedSignatureDocument(document);
}
export function signingStateOf(document, relationship) {
    const fullySigned = isFullySigned(document, relationship);
    if (isFinalized(relationship) && awaitsSignedCopy(document, relationship)) {
        return 'awaitingSignatures';
    }
    if (fullySigned || isMandatorySignature(document) && isFinalized(relationship)) {
        return 'signed';
    }
    const signedByAnyone = boundSigningOrdinals(document, relationship).some((ordinal)=>isSignatureCompletedBy(document, ordinal));
    return !signedByAnyone || isMandatorySignature(document) ? 'notSigned' : 'partiallySigned';
}
export function zipReadiness(relationship) {
    const documents = signingDocumentsOf(relationship);
    if (!isFinalized(relationship) || !documents.some((document)=>isFullySigned(document, relationship))) {
        return 'none';
    }
    return documents.some((document)=>awaitsSignedCopy(document, relationship)) ? 'preparing' : 'ready';
}
export function signedPersonsOf(document, relationship) {
    return boundSigningOrdinals(document, relationship).filter((ordinal)=>isSignatureCompletedBy(document, ordinal)).map((ordinal)=>personOfOrdinal(relationship, ordinal, actorOf(document, ordinal)));
}
export function signingAnomaliesOf(relationship) {
    return signingDocumentsOf(relationship).flatMap((document)=>compact([
            isFinalized(relationship) && isMandatorySignature(document) && !isFullySigned(document, relationship) && 'requiredNotCompletedAfterApproval',
            actedOrdinals(document).some((ordinal)=>!isDefinedRole(document, ordinal)) && 'actionOnUnboundOrdinal'
        ]).map((kind)=>({
                kind,
                legalTermsId: document.legalTermsId
            })));
}
export function name(legalTerms) {
    return legalTerms.name;
}
export function textId(legalTerms) {
    var _legalTerms_richTextId;
    if (isPDF(legalTerms)) {
        return undefined;
    }
    return (_legalTerms_richTextId = legalTerms.richTextId) !== null && _legalTerms_richTextId !== void 0 ? _legalTerms_richTextId : undefined;
}
